import type { CreateOperationConfig } from "../../crud-operation/create-operation";
import type { CrudOperation } from "../../crud-operation/crud-operation";
import type { DeleteOperationConfig } from "../../crud-operation/delete-operation";
import type { UpdateOperationConfig } from "../../crud-operation/update-operation";
import type { ApiService } from "../api/api-service";
import type { MutationRefetchQueries } from "../api/mutation";
import type { Services } from "../services";
import { bound, notify, observable } from "ecce-react";
import { UpdateOperation } from "../../crud-operation/update-operation";
import { DeleteOperation } from "../../crud-operation/delete-operation";
import { CreateOperation } from "../../crud-operation/create-operation";


export type CrudOperationOptions<EditResult> = Partial<{
	refetchQueries: MutationRefetchQueries;
	onSubmitted: (result: EditResult) => void;
}>;


type ActiveOperation = {
	readonly operation: CrudOperation<unknown>;
	readonly options: Readonly<CrudOperationOptions<unknown>>;
};

export type CrudServiceDependencies = {
	api: ApiService;
};
/**
 * Manages displaying & submitting CRUD operation dialogs.
 *
 * Use {@link beginUpdate()} & {@link beginCreate()} for straightforward
 * operations which do not require customisation outside of the UI.
 *
 * Use {@link begin()} and pass an implementation of {@link CrudOperation} to begin
 * a custom operation.
 */
export class CrudService {
	#activeOperation: ActiveOperation | null = null;
	@observable() get activeOperation(): CrudOperation<unknown> | null { return this.#activeOperation?.operation ?? null; }

	#loading = false;
	get loading(): boolean { return this.#loading; }
	@observable() private set loading(value: boolean) { this.#loading = value; }

	#services: Services | null = null;

	/**
	 * Called by Services.
	 */
	private initialise(services: Services) {
		this.#services = services;
	}

	/**
	 * Begin a custom CRUD operation.
	 *
	 * @see {@link CrudOperation}
	 */
	async begin<T>(operation: CrudOperation<T>, options: Readonly<CrudOperationOptions<T>> = {}): Promise<void> {
		this.cancel(true);

		operation["initialiseOperation"](this.#services!, options);
		this.#activeOperation = {
			operation: operation as CrudOperation<unknown>,
			options: options as CrudOperationOptions<unknown>,
		};
		notify(this, "activeOperation");

		this.loading = true;
		this.#services?.notification.dismissAll();
		try {
			await operation.initialise();
		} finally {
			this.loading = false;
		}
	}

	/**
	 * Begin a simple create operation.
	 */
	beginCreate<Attributes, EditResult>(config: CreateOperationConfig<Attributes, EditResult>, options?: Readonly<CrudOperationOptions<EditResult>>): void {
		this.begin(new CreateOperation(config) as CrudOperation<EditResult>, options);
	}

	/**
	 * Begin a simple update operation.
	 */
	beginUpdate<Attributes, Result>(config: UpdateOperationConfig<Attributes, Result>, options?: Readonly<CrudOperationOptions<Result>>): void {
		this.begin(new UpdateOperation(config) as CrudOperation<Result>, options);
	}

	/**
	 * Begin a simple delete operation.
	 */
	beginDelete(config: DeleteOperationConfig, options?: Readonly<CrudOperationOptions<null>>): void {
		this.begin(new DeleteOperation(config), options);
	}

	/**
	 * Submits the currently active operation.
	 *
	 * Is @bound().
	 */
	@bound()
	async submit(): Promise<void> {
		if(!this.#activeOperation) {
			return;
		}

		this.loading = true;
		let operationEnded = false;
		try {
			const result = await this.#activeOperation.operation.submit({
				refetchQueries: this.#activeOperation.options.refetchQueries as any, // eslint-disable-line @typescript-eslint/no-explicit-any
			});

			switch(result.status) {
				case "ok": {
					this.#services?.notification.send({
						severity: "success",
						title: this.#activeOperation.operation.successMessage,
					});
					this.#activeOperation.options?.onSubmitted?.(result.value);
					operationEnded = true;
				}
				break;
				case "invalid": {
					operationEnded = false;
					break;
				}
				case "unchanged": {
					this.#services?.notification.send({
						severity: "info",
						titleKey: "common:noChangesToSave",
					});

					operationEnded = true;
					break;
				}
			}
		} finally {
			this.loading = false;
			if(operationEnded) {
				this.#endActiveOperation();
			}
		}
	}

	#endActiveOperation() {
		this.#activeOperation?.operation.dispose();
		this.#activeOperation = null;
		notify(this, "activeOperation");
	}

	/**
	 * Called by CrudOperation to manage custom loading states.
	 */
	private setLoading(loading: boolean): void {
		this.loading = loading;
	}

	/**
	 * Cancel the currently active operation.
	 */
	@bound()
	async cancel(force = false): Promise<void> {
		if(!this.activeOperation) {
			return;
		}

		if(this.activeOperation.hasChanges() && !force) {
			const confirm = await this.#services!.prompt.confirmDiscardUnsavedChanges();
			if(!confirm) {
				return;
			}
		}

		this.#endActiveOperation();
	}
}
